import React, { useEffect, useMemo, useState, useCallback } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Form,
    Row,
} from "reactstrap"
import { useFormik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { validateBeforeConfirm } from "helpers/validation_helper"
import { getSupportedBanks } from "helpers/backend_helper"
import {
    getCitiesAction as onGetCities,
} from "store/actions"
import ConfirmButton from "components/Common/ConfirmButton"
import TextInput from "components/Common/TextInput"
import SelectInput from "components/Common/SelectInput"
import CheckboxInput from "components/Common/CheckboxInput"
import CButton from "components/Common/CButton"
import IconButton from "components/Common/IconButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"

const BankingForm = ({
    page,
    match,
    history,
    onTabCompletion,
    activeTab,
    tabId,
    onFormRef,
    createdEntityID,
    entityType = "merchant",
    getBankingListAction,
    addBankingAction,
    manageBankingAction, // For activate/deactivate
    toggleDefaultBankingAction, // For toggle default
    checkBankingStatusAction, // For checking account status
    clearBankingListAction,
    // Redux selectors
    bankingListSelector,
    bankingErrorSelector,
    bankingCreationSuccessSelector,
    // API configuration
    apiContextUri,
    toggleApiContextUri,
    toggleDefaultApiContextUri,
    checkStatusApiContextUri,
    // Labels and text
    entityName = "Merchant",
}) => {
    const [showAddForm, setShowAddForm] = useState(page === "add")
    const [isCreating, setIsCreating] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [previousBankingLength, setPreviousBankingLength] = useState(0)

    const detailID = match?.params?.id
    const dispatch = useDispatch()

    // Helper function to check if error should be displayed
    const shouldDisplayError = error => {
        if (!error) return false
        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""
        const noDataPatterns = ["no content available"]
        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    const { data, appInfo, currencies, cities, countries } = useSelector(
        state => ({
            data: state[entityType === "merchant" ? "merchants" : "merchantGroup"],
            appInfo: state.appInfo.appInfo,
            currencies: state.appInfo?.appInfo?.currencies || [],
            cities: state.appInfo.cities || [],
            countries: state.appInfo?.appInfo?.countries || [],
        })
    )

    // Use dynamic selectors based on entity type
    const bankingList = useSelector(bankingListSelector) || []

    const bankingError = useSelector(bankingErrorSelector)
    const creationSuccess = useSelector(bankingCreationSuccessSelector)

    // Use createdEntityID in add mode, detailID in detail mode (memoized)
    const currentEntityID = useMemo(() =>
        page === "add" ? createdEntityID : detailID
        , [page, createdEntityID, detailID])

    // Get field configurations based on entity type (memoized to prevent rerenders)
    const fieldConfig = useMemo(() => {
        if (entityType === "merchantGroup") {
            return {
                idField: "merchantGroupBankingID",
                entityIdField: "merchantGroupID",
            }
        }
        return {
            idField: "merchantBankingID",
            entityIdField: "merchantID",
        }
    }, [entityType])

    // Fetch banking list for the current entity only when tab is active
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (currentEntityID && getBankingListAction && isTabActive) {
            dispatch(
                getBankingListAction({
                    headers: {
                        "input-parameters": { [fieldConfig.entityIdField]: currentEntityID },
                    },
                    // encDecAlgo: "BASE64",
                })
            )
        }
    }, [dispatch, currentEntityID, getBankingListAction, fieldConfig.entityIdField, activeTab, tabId])

    // Clear banking data when component unmounts
    useEffect(() => {
        return () => {
            if (clearBankingListAction) {
                dispatch(clearBankingListAction())
            }
        }
    }, [dispatch, clearBankingListAction])

    // Fetch dropdown data
    useEffect(() => {
        if (!cities.length && appInfo?.appCountryID) {
            dispatch(
                onGetCities({
                    headers: {
                        "input-parameters": { countryIDs: [appInfo.appCountryID] },
                    },
                    // encDecAlgo: "BASE64",
                })
            )
        }
    }, [dispatch, cities.length, appInfo?.appCountryID])

    // Fetch supported banks for the Bank Identifier dropdown (CMS.Banking requirement:
    // bankIdentifier is mandatory on createMerchantGroupBanking/createMerchantBanking).
    // resourceType MERCHANT_GROUP verified against staging 2026-07-08; MERCHANT mirrors it.
    const [supportedBanks, setSupportedBanks] = useState([])
    useEffect(() => {
        if (!showAddForm || !currentEntityID) return
        const resourceType = entityType === "merchantGroup" ? "MERCHANT_GROUP" : "MERCHANT"
        let cancelled = false
        getSupportedBanks({
            headers: {
                // supportedBanks accepts ONLY record-count 25 (anything else
                // returns 416; verified against staging 2026-08-20).
                // start-index is zero-based (API spec: integer >= 0, default 0;
                // Sudip, 2 Oct 2026): 1 skips the first record.
                "start-index": "0",
                "record-count": "25",
                "input-parameters": { resourceType, resourceID: String(currentEntityID) },
            },
        })
            .then(res => {
                if (cancelled) return
                const rows = Array.isArray(res) ? res : (res?.data || [])
                setSupportedBanks(rows)
            })
            .catch(() => { if (!cancelled) setSupportedBanks([]) })
        return () => { cancelled = true }
    }, [showAddForm, entityType, currentEntityID])

    // Options for dropdowns
    const currencyOptions = useMemo(() => {
        return currencies.map(currency => ({
            value: currency.isoCode,
            label: `${currency.isoCode} - ${currency.name}`,
        }))
    }, [currencies])

    const cityOptions = useMemo(() => {
        return cities.map(city => ({
            value: city.cityID,
            label: city.name,
        }))
    }, [cities])

    // Bank Identifier dropdown options from the supportedBanks lookup
    const bankOptions = useMemo(() => {
        return (supportedBanks || [])
            .filter(b => b && b.bankIdentifier && b.bankName)
            .map(b => ({ value: String(b.bankIdentifier), label: b.bankName }))
    }, [supportedBanks])


    // Get validation schema
    const getValidationSchema = () => {
        return Yup.object({
            accountLegalName: Yup.string()
                .required("Please Enter Account Legal Name")
                .max(128, "Account Legal Name cannot be greater than 128 characters"),
            accountDisplayName: Yup.string()
                .required("Please Enter Account Display Name")
                .max(64, "Account Display Name cannot be greater than 64 characters"),
            accountRegisteredAddress: Yup.string()
                .required("Please Enter Account Registered Address")
                .max(256, "Account Registered Address cannot be greater than 256 characters"),
            accountCurrencyCode: Yup.object().nullable().required("Please Select Account Currency Code"),
            bankAccountNumber: Yup.string()
                .required("Please Enter Bank Account Number")
                .matches(/^\d+$/, "Bank Account Number must contain only numbers"),
            iban: Yup.string()
                .required("Please Enter IBAN")
                .max(64, "IBAN cannot be greater than 64 characters"),
            bankIdentifier: Yup.mixed()
                .test("bankIdentifier-required", "Please Select Bank Identifier", value =>
                    Boolean(value && (typeof value === "object" ? value.value : value))
                ),
            bankCityName: Yup.object().nullable().required("Please Select Bank City Name"),
            defaultSettlementAccount: Yup.boolean(),
        })
    }

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            actionType: "create",
            [fieldConfig.idField]: "",
            [fieldConfig.entityIdField]: currentEntityID || "",
            accountLegalName: "",
            accountDisplayName: "",
            accountRegisteredAddress: "",
            accountCurrencyCode: null,
            bankAccountNumber: "",
            iban: "",
            bankIdentifier: null,
            bankCityName: null,
            defaultSettlementAccount: false,
        },
        validationSchema: getValidationSchema(),
        onSubmit: async (values) => {
            const isValid = await validateBeforeConfirm(validation)
            if (isValid) {
                handleAddBanking(values)
            }
        },
    })


    // Monitor for successful banking creation and reset form
    useEffect(() => {
        if (bankingList.length > previousBankingLength && !bankingError) {
            setTimeout(() => {
                validation.resetForm()
                setIsSubmitting(false)
                setPreviousBankingLength(bankingList.length)

                if (page === "add") {

                    // In add mode: keep form visible but reset it
                    // Form will stay visible for additional banking accounts
                    setIsCreating(false)
                } else if (isCreating) {
                    // In detail mode: close the add form after creation
                    setIsCreating(false)
                    setShowAddForm(false)
                }
            }, 500)
        } else if (bankingError && isSubmitting) {
            setIsSubmitting(false)
        }
    }, [
        bankingError,
        bankingList.length,
        previousBankingLength,
        isSubmitting,
        page,
        isCreating,
    ])

    const handleAddBanking = async values => {
        if (!currentEntityID || (page !== "add" && !isCreating)) {
            console.error("Banking creation not allowed")
            return
        }

        setIsSubmitting(true)

        // Find the country's isoCode3D from the countries array
        const currentCountry = countries.find(c => c.countryID === appInfo?.appCountryID)
        const bankCountryCode = currentCountry?.isoCode3D || ""

        const basePayload = {
            actionType: "create",
            [fieldConfig.entityIdField]: currentEntityID.toString(),
            accountLegalName: values.accountLegalName,
            accountDisplayName: values.accountDisplayName,
            accountRegisteredAddress: values.accountRegisteredAddress,
            accountCurrencyCode: values.accountCurrencyCode?.value || "",
            bankAccountNumber: values.bankAccountNumber.toString(),
            bankCountryCode: bankCountryCode,
            iban: values.iban,
            bankIdentifier: (values.bankIdentifier && typeof values.bankIdentifier === "object")
                ? values.bankIdentifier.value
                : (values.bankIdentifier || ""),
            bankCityName: values.bankCityName?.label || "",
            defaultSettlementAccount: values.defaultSettlementAccount.toString(),
        }

        const options = {}

        if (addBankingAction) {
            dispatch(addBankingAction(basePayload, options, null))
        }
    }

    const handleActivateBanking = async bankingId => {
        if (!currentEntityID) return

        setIsSubmitting(true)

        const payload = {
            [fieldConfig.entityIdField]: currentEntityID.toString(),
            [fieldConfig.idField]: bankingId,
            actionType: "activate",
        }

        if (manageBankingAction) {
            dispatch(manageBankingAction(payload, {}, null))
        }
        setIsSubmitting(false)
    }

    const handleDeactivateBanking = async bankingId => {
        if (!currentEntityID) return

        setIsSubmitting(true)

        const payload = {
            [fieldConfig.entityIdField]: currentEntityID.toString(),
            [fieldConfig.idField]: bankingId,
            actionType: "deactivate",
        }

        if (manageBankingAction) {
            dispatch(manageBankingAction(payload, {}, null))
        }
        setIsSubmitting(false)
    }

    const handleSetDefaultBanking = async bankingId => {
        if (!currentEntityID) return

        setIsSubmitting(true)

        const payload = {
            [fieldConfig.entityIdField]: currentEntityID.toString(),
            [fieldConfig.idField]: bankingId,
            actionType: "setDefault",
        }

        if (toggleDefaultBankingAction) {
            dispatch(toggleDefaultBankingAction(payload, {}, null))
        }
        setIsSubmitting(false)
    }

    const handleUnsetDefaultBanking = async bankingId => {
        if (!currentEntityID) return

        setIsSubmitting(true)

        const payload = {
            [fieldConfig.entityIdField]: currentEntityID.toString(),
            [fieldConfig.idField]: bankingId,
            actionType: "unsetDefault",
        }

        if (toggleDefaultBankingAction) {
            dispatch(toggleDefaultBankingAction(payload, {}, null))
        }
        setIsSubmitting(false)
    }

    const handleCheckBankingStatus = async (destinationID) => {
        if (!destinationID || !checkBankingStatusAction) return

        dispatch(checkBankingStatusAction(destinationID))
    }

    const handleAddNewBanking = useCallback(() => {
        setIsCreating(true)
        setShowAddForm(true)
    }, [])

    const handleCancelBanking = useCallback(() => {
        if (page === "detail") {
            setShowAddForm(false)
        }
        validation.resetForm()
    }, [page, validation])

    // Get table columns (memoized to prevent rerenders)
    const getTableColumns = useCallback(() => entityType === "merchant" ? [
        {
            key: "accountLegalName",
            label: "Account Legal Name",
        },
        {
            key: "accountDisplayName",
            label: "Account Display Name",
        },
        {
            key: "accountCurrencyCode",
            label: "Currency",
        },
        {
            key: "bankAccountNumber",
            label: "Account Number",
        },
        {
            key: "iban",
            label: "IBAN",
        },
        {
            key: "bankIdentifier",
            label: "Bank Identifier",
        },
        {
            key: "status",
            label: "Status",
            render: item => (

                item.accountStatus === 'active' ? 'Active' : 'Inactive'

            )
        },
        (entityType === "merchant" ? ({
            key: "settlementLevel",
            label: "Settlement Level",
            render: item => (

                !item.settlementLevel ? '-' : item.settlementLevel

            )
        }) : ""),
        {
            key: "defaultSettlementAccount",
            label: "Default Account",
            render: item => (

                item.defaultSettlementAccount === "true" ? 'Yes' : 'No'

            )
        },
        {
            key: "payoutProviderDestinationStatus",
            label: "Account Status",

        }
    ] : [
        {
            key: "accountLegalName",
            label: "Account Legal Name",
        },
        {
            key: "accountDisplayName",
            label: "Account Display Name",
        },
        {
            key: "accountCurrencyCode",
            label: "Currency",
        },
        {
            key: "bankAccountNumber",
            label: "Account Number",
        },
        {
            key: "iban",
            label: "IBAN",
        },
        {
            key: "bankIdentifier",
            label: "Bank Identifier",
        },
        {
            key: "status",
            label: "Status",
            render: item => (

                item.accountStatus === 'active' ? 'Active' : 'Inactive'

            )
        },
        {
            key: "defaultSettlementAccount",
            label: "Default Account",
            render: item => (

                item.defaultSettlementAccount === "true" ? 'Yes' : 'No'

            )
        },
        {
            key: "payoutProviderDestinationStatus",
            label: "Account Status",

        }
    ], [])

    return (
        <>
            {bankingError && shouldDisplayError(bankingError) && (
                <Alert color="danger" className="mb-3">
                    {typeof bankingError === "string"
                        ? bankingError
                        : bankingError.data || bankingError.description ||
                        bankingError.message ||
                        "An error occurred"}
                </Alert>
            )}

            {/* Add/Edit Form */}
            {showAddForm && (
                <Card className="mb-4">
                    <CardBody>
                        <h6 className="mb-3">Add New Banking Account</h6>
                        <Form
                            className="needs-validation"
                            onSubmit={e => {
                                e.preventDefault()
                                validation.handleSubmit()
                                return false
                            }}
                        >
                            <Row>
                                {/* Account Legal Name */}
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="accountLegalName"
                                        placeholder="Enter Account Legal Name"
                                        label="Account Legal Name"
                                        helperText="Max 128 chars allowed"
                                        maxLength={128}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Account Display Name */}
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="accountDisplayName"
                                        placeholder="Enter Account Display Name"
                                        label="Account Display Name"
                                        helperText="Max 64 chars allowed"
                                        maxLength={64}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Account Registered Address */}
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="accountRegisteredAddress"
                                        placeholder="Enter Account Registered Address"
                                        label="Account Registered Address"
                                        helperText="Max 256 chars allowed"
                                        maxLength={256}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Account Currency Code */}
                                <Col md="6">
                                    <SelectInput
                                        name="accountCurrencyCode"
                                        label="Account Currency Code"
                                        placeholder="Select Currency..."
                                        options={currencyOptions}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Bank Account Number */}
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="bankAccountNumber"
                                        placeholder="Enter Bank Account Number"
                                        label="Bank Account Number"
                                        helperText="Numbers only"
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* IBAN */}
                                <Col md="6">
                                    <TextInput
                                        type="text"
                                        name="iban"
                                        placeholder="Enter IBAN"
                                        label="IBAN"
                                        helperText="Max 64 chars allowed"
                                        maxLength={64}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Bank Identifier — mandatory per CMS.Banking. Dropdown from the
                                    supportedBanks lookup; free-form fallback if the lookup is
                                    unavailable so banking creation is never blocked. */}
                                <Col md="6">
                                    {bankOptions.length > 0 ? (
                                        <SelectInput
                                            name="bankIdentifier"
                                            label="Bank Identifier"
                                            placeholder="Select Bank..."
                                            options={bankOptions}
                                            validation={validation}
                                            required={true}
                                        />
                                    ) : (
                                        <TextInput
                                            type="text"
                                            name="bankIdentifier"
                                            placeholder="Enter Bank Identifier"
                                            label="Bank Identifier"
                                            helperText="Supported-banks lookup unavailable: enter the identifier manually"
                                            validation={validation}
                                            required={true}
                                        />
                                    )}
                                </Col>

                                {/* Bank City Name */}
                                <Col md="6">
                                    <SelectInput
                                        name="bankCityName"
                                        label="Bank City Name"
                                        placeholder="Select City..."
                                        options={cityOptions}
                                        validation={validation}
                                        required={true}
                                    />
                                </Col>

                                {/* Default Settlement Account */}
                                <Col md="12">
                                    <CheckboxInput
                                        name="defaultSettlementAccount"
                                        validation={validation}
                                        hideInReadonlyWhenEmpty={true}
                                        dependentField="accountLegalName"
                                        trueText="Enabled"
                                        falseText="Disabled"
                                        checkboxLabel="Default Settlement Account"
                                    />
                                </Col>
                            </Row>

                            <div className="text-end">
                                {
                                    page !== "add" ? (
                                        <CButton.Secondary
                                            text="Cancel"
                                            className="me-2"
                                            onClick={handleCancelBanking}
                                            disabled={isSubmitting}
                                        />) : null
                                }

                                <ConfirmButton
                                    contextUri={apiContextUri}
                                    actionType="post"
                                    validateBeforeConfirm={() => validateBeforeConfirm(validation)}
                                    onConfirm={() => handleAddBanking(validation.values)}
                                    message={`Are you sure you want to add this banking account?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <CButton.Primary
                                            text={`Add Banking Account`}
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                        />
                                    }
                                />
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            )}

            {/* Banking List */}
            <SimpleTableContainer
                title={`Banking Accounts`}
                data={bankingList.map(banking => ({
                    ...banking,
                    id: banking[fieldConfig.idField],
                }))}
                columns={getTableColumns()}
                loading={false}
                emptyMessage={`No banking accounts added yet`}
                emptyIcon="bx bx-credit-card"
                customActions={item => (
                    <>
                        {item.accountStatus === 'active' ? (
                            <ConfirmButton
                                contextUri={toggleApiContextUri}
                                actionType="post"
                                onConfirm={() => handleDeactivateBanking(item[fieldConfig.idField])}
                                message={`Are you sure you want to deactivate this  banking account?`}
                                disabled={isSubmitting}
                                renderButton={
                                    <IconButton
                                        icon="bx bx-no-entry"
                                        tooltip="Deactivate"
                                        color="warning"
                                        title="Deactivate"
                                    />
                                }
                            />
                        ) : (
                            <ConfirmButton
                                contextUri={toggleApiContextUri}
                                actionType="post"
                                onConfirm={() => handleActivateBanking(item[fieldConfig.idField])}
                                message={`Are you sure you want to activate this  banking account? This will deactivate other active accounts.`}
                                disabled={isSubmitting}
                                renderButton={
                                    <IconButton
                                        icon="bx bx-play"
                                        color="success"
                                        title={"Activate"}
                                    />
                                }
                            />
                        )}

                        {/* Add spacing between activate/deactivate and default buttons */}
                        <span className="me-2"></span>

                        {/* Toggle Default Button - Show for both active and inactive accounts */}
                        {item.accountStatus === 'active' ? (
                            // Active accounts - functional buttons
                            item.defaultSettlementAccount === "true" ? (
                                <ConfirmButton
                                    contextUri={toggleDefaultApiContextUri}
                                    actionType="post"
                                    onConfirm={() => handleUnsetDefaultBanking(item[fieldConfig.idField])}
                                    message={`Are you sure you want to unset this as the default banking account?`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <IconButton
                                            icon="bx bxs-star"
                                            tooltip="Unset Default"
                                            color="secondary"
                                            title="Unset Default"
                                        />
                                    }
                                />
                            ) : (
                                <ConfirmButton
                                    contextUri={toggleDefaultApiContextUri}
                                    actionType="post"
                                    onConfirm={() => handleSetDefaultBanking(item[fieldConfig.idField])}
                                    message={`Are you sure you want to set this as the default banking account? This will unset other default accounts.`}
                                    disabled={isSubmitting}
                                    renderButton={
                                        <IconButton
                                            icon="bx bx-star"
                                            tooltip="Set as Default"
                                            color="secondary"
                                            title="Set as Default"
                                        />
                                    }
                                />
                            )
                        ) : (
                            // Inactive accounts - disabled buttons
                            item.defaultSettlementAccount === "true" ? (
                                <IconButton
                                    icon="bx bxs-star"
                                    tooltip="Default account (inactive)"
                                    color="secondary"
                                    title="Default (Inactive)"
                                    disabled={true}
                                />
                            ) : (
                                <IconButton
                                    icon="bx bx-star"
                                    tooltip="Cannot set default (inactive account)"
                                    color="secondary"
                                    title="Set Default (Disabled)"
                                    disabled={true}
                                />
                            )
                        )}

                        {/* Add spacing between default and status check buttons */}
                        <span className="me-2"></span>

                        {/* Check Account Status Button */}
                        {item.payoutProviderDestinationID && checkBankingStatusAction && (
                            <AccessControlWrapper
                                contextUri={checkStatusApiContextUri}
                                actionType="get"
                            >
                                <IconButton
                                    icon="mdi mdi-refresh"
                                    tooltip="Check Account Status"
                                    color="info"
                                    title="Check Status"
                                    onClick={() => handleCheckBankingStatus(item.payoutProviderDestinationID)}
                                />
                            </AccessControlWrapper>
                        )}
                    </>
                )}
                headerActions={
                    page !== "add" &&
                    !showAddForm && (
                        <AccessControlWrapper
                            contextUri={apiContextUri}
                            actionType="post"
                        >
                            <CButton.Success
                                icon="bx bx-plus"
                                text={`Add Banking Account`}
                                onClick={handleAddNewBanking}
                            />
                        </AccessControlWrapper>
                    )
                }
            />
        </>
    )
}

export default BankingForm